import { useGetSegmentQuery } from 'api/segments'
import SegmentDetailsModal from 'components/AISegmentBuilder/SegmentDetailsModal'
import ErrorPage from 'components/ErrorPage'
import { useHeaderContext } from 'components/Header'
import useSegmentCampaign from 'hooks/useSegmentCampaign'
import { useSegmentDetails } from 'hooks/useSegmentDetails'
import { buildTranslate } from 'locales'
import { useEffect } from 'react'
import Helmet from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import NewSegmentCampaignModal from 'scenes/CampaignCenter/Landing/NewSegmentCampaignModal'
import { updateMessages } from 'slices/aiConversations'
import {
  closeCampaignModal,
  closeDetailsAndOpenCampaignModal,
  closeSegmentDetailsModal,
  selectAISegmentBuilder,
  setConversationState,
  setInitialPrompt,
} from 'slices/aiSegmentBuilder'
import { resetAllPickers } from 'slices/aiSegmentBuilder/picker'
import { history } from 'store'
import './AISegmentBuilder.scss'
import AISegmentBuilderConversation from './AISegmentBuilderConversation'
import AISegmentBuilderEmptyState from './AISegmentBuilderEmptyState'
import AISegmentBuilderHeader from './AISegmentBuilderHeader'

const t = buildTranslate('segments.ai_builder')

const AISegmentBuilderPage = () => {
  const { setShouldShow: setHeaderShow } = useHeaderContext()
  const dispatch = useDispatch()
  const {
    campaignModal,
    segmentDetailsModal,
    conversationState,
    initialPrompt,
  } = useSelector(selectAISegmentBuilder)
  const { createCampaign } = useSegmentCampaign()
  const { data: segmentData, isError } = useGetSegmentQuery(
    { segmentId: String(campaignModal.segmentId) },
    { skip: !campaignModal.segmentId }
  )
  const {
    data: detailsData,
    loading: detailsLoading,
    error: detailsError,
  } = useSegmentDetails({
    segmentId: segmentDetailsModal.segmentId
      ? String(segmentDetailsModal.segmentId)
      : '',
    // SegmentDetails fetches sample customers for this segment via its own Redux action
    enableSampleCustomers: false,
    // The AI can edit a segment in place, so the modal must not trust the cache
    // from the last time it was opened (AI-257).
    refetchOnMountOrArgChange: true,
    skip: !segmentDetailsModal.segmentId,
  })
  // Hide the global header when this page is mounted
  useEffect(() => {
    setHeaderShow(false)
    return () => {
      setHeaderShow(true)
    }
  }, [setHeaderShow])

  useEffect(() => {
    return () => {
      dispatch(closeCampaignModal())
      dispatch(closeSegmentDetailsModal())
      // Clear persisted messages when leaving the AI segment builder
      dispatch(updateMessages([]))
      // Reset conversation state to empty and clear initial prompt
      dispatch(setConversationState('empty'))
      dispatch(setInitialPrompt(''))
      // Clear all picker state to prevent stale data on next visit
      dispatch(resetAllPickers())
    }
  }, [dispatch])

  const handlePromptSubmit = (prompt: string) => {
    dispatch(setInitialPrompt(prompt))
    dispatch(setConversationState('chatting'))
  }

  const handleClose = () => {
    history.push('/segments')
  }

  const handleModalClose = () => {
    dispatch(closeCampaignModal())
  }

  const handleDetailsClose = () => {
    dispatch(closeSegmentDetailsModal())
  }

  const handleDetailsCreateCampaign = () => {
    if (segmentDetailsModal.segmentId) {
      dispatch(closeDetailsAndOpenCampaignModal(segmentDetailsModal.segmentId))
    }
  }

  if (isError) {
    return <ErrorPage dataTestId="segment-error-page" />
  }

  return (
    <div className="ai-segment-builder">
      <Helmet>
        <title>{t('page_title')}</title>
      </Helmet>

      <AISegmentBuilderHeader onClose={handleClose} />

      <div className="ai-segment-builder__content pt-xxl">
        {conversationState === 'empty' && (
          <AISegmentBuilderEmptyState onPromptSubmit={handlePromptSubmit} />
        )}
        {conversationState === 'chatting' && (
          <AISegmentBuilderConversation initialPrompt={initialPrompt} />
        )}
      </div>

      {segmentDetailsModal.isOpen && segmentDetailsModal.segmentId && (
        <SegmentDetailsModal
          isOpen={segmentDetailsModal.isOpen}
          segment={detailsData.segment}
          title={segmentDetailsModal.title ?? undefined}
          loading={detailsLoading.segment}
          error={!!detailsError.segment}
          onClose={handleDetailsClose}
          onCreateCampaign={handleDetailsCreateCampaign}
        />
      )}

      <NewSegmentCampaignModal
        isOpen={campaignModal.isOpen}
        onClose={handleModalClose}
        segment={segmentData?.target_segment || null}
        onCreate={createCampaign}
      />
    </div>
  )
}

export default AISegmentBuilderPage
